/**
 * Theme Context for Dynamic Carbon Design System Theming
 * Provides system preference detection and user preference persistence
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type CarbonTheme = 'white' | 'g10' | 'g90' | 'g100';

interface ThemeContextType {
  theme: CarbonTheme;
  setTheme: (theme: CarbonTheme) => void;
  isSystemPreference: boolean;
  toggleSystemPreference: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const getSystemPreferredTheme = (): CarbonTheme => {
  if (typeof window === 'undefined') return 'white';

  const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  return isDark ? 'g90' : 'white';
};

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: CarbonTheme;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, defaultTheme }) => {
  const [isSystemPreference, setIsSystemPreference] = useState(() => {
    const stored = localStorage.getItem('useSystemTheme');
    return stored !== 'false';
  });

  const [theme, setThemeState] = useState<CarbonTheme>(() => {
    if (isSystemPreference) {
      return getSystemPreferredTheme();
    }
    const storedTheme = localStorage.getItem('carbonTheme') as CarbonTheme;
    return storedTheme || defaultTheme || 'white';
  });

  useEffect(() => {
    if (isSystemPreference) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      const handleChange = (e: MediaQueryListEvent) => {
        setThemeState(e.matches ? 'g90' : 'white');
      };

      setThemeState(getSystemPreferredTheme());
      mediaQuery.addEventListener('change', handleChange);

      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [isSystemPreference]);

  const setTheme = (newTheme: CarbonTheme) => {
    setThemeState(newTheme);
    localStorage.setItem('carbonTheme', newTheme);
    setIsSystemPreference(false);
    localStorage.setItem('useSystemTheme', 'false');
  };

  const toggleSystemPreference = () => {
    const newValue = !isSystemPreference;
    setIsSystemPreference(newValue);
    localStorage.setItem('useSystemTheme', String(newValue));

    if (newValue) {
      setThemeState(getSystemPreferredTheme());
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, isSystemPreference, toggleSystemPreference }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
